/**
 * useGameMessageBridge.js
 *
 * 用於管理遊戲頁面與 HiTeach / HiTeachCC 的通信橋接器
 * 包含使用者驗證、訊息監聽器和清理功能
 *
 * 支援的訊息來源：
 * - Init.GameHome: 來自 HiTeach 桌面版 WebView
 * - HiTeachCC.Auth: 來自 HiTeachCC iframe 嵌入
 */

import { onMounted, onUnmounted } from 'vue';
import jwt_decode from 'jwt-decode';

/**
 * HiTeachCC Origin 白名單
 * 用於驗證 postMessage 來源的安全性
 */
const HITEACHCC_ALLOWED_ORIGINS = [
    // Global 站點
    'https://hiteachcc.teammodel.net',
    'https://hiteachcc-rc.teammodel.net',
    'https://hiteachcc-test.teammodel.net',
    // CN 站點
    'https://hiteachcc.teammodel.cn',
    'https://hiteachcc-rc.teammodel.cn',
    'https://hiteachcc-test.teammodel.cn',
    // 本地開發
    'http://localhost:8080',
    'http://localhost:5173'
];

/**
 * MessageBridge 工具類
 * 處理 WebView 和 Web 環境的訊息通信
 */
const MessageBridge = {
    // 環境類型
    ENV: {
        WEBVIEW: 'webview',
        WEB: 'web'
    },
    // 當前環境
    currentEnv: 'webview',
    // 初始化
    init() {
        this.currentEnv = window?.chrome?.webview ? this.ENV.WEBVIEW : this.ENV.WEB;
        return this;
    },
    // 發送消息
    postMessage(data, origin = '*') {
        if (this.currentEnv === this.ENV.WEBVIEW) {
            window?.chrome?.webview.postMessage(data);
        } else {
            window.postMessage(data, origin);
        }
    },
    // 發送消息給父視窗（iframe 專用）
    postMessageToParent(data, origin = '*') {
        try {
            if (window.parent && window.parent !== window) {
                window.parent.postMessage(data, origin);
            }
        } catch (e) {
            // 跨域時可能會丟例外，忽略即可
            console.warn('[MessageBridge] postMessageToParent 失敗:', e);
        }
    },
    // 添加消息監聽器
    addEventListener(callback) {
        if (this.currentEnv === this.ENV.WEBVIEW) {
            window?.chrome?.webview.addEventListener('message', event => {
                callback(event);
            });
        } else {
            window.addEventListener('message', event => {
                callback(event);
            });
        }
    },
    // 移除消息監聽器
    removeEventListener(callback) {
        if (this.currentEnv === this.ENV.WEBVIEW) {
            window?.chrome?.webview.removeEventListener('message', callback);
        } else {
            window.removeEventListener('message', callback);
        }
    }
};

/**
 * 檢查是否在 iframe 環境中
 */
const isInIframe = () => {
    try {
        return window.self !== window.top;
    } catch (e) {
        return true; // 如果拋出異常，說明在跨域 iframe 中
    }
};

/**
 * 設定使用者驗證資料（來自 HiTeach Init.GameHome）
 * @param {Object} authData - 包含 id_Token, access_Token, expires_in, login_Id, school_vers 等欄位
 * @param {String} gameName - 遊戲名稱，用於 console.log 識別
 */
export const setupUserAuth = (authData, gameName = 'Game') => {
    try {
        console.log(`[${gameName}] 設定使用者驗證資料:`, authData);

        // 儲存登入資料到 localStorage（使用標準鍵值）
        if (authData.id_Token) {
            localStorage.setItem('id_token', authData.id_Token);
        }
        if (authData.access_Token) {
            localStorage.setItem('access_token', authData.access_Token);
        }
        if (authData.expires_in) {
            localStorage.setItem('expires_in', authData.expires_in);
        }

        if (authData?.hostSoftwareVersion) {
            sessionStorage.setItem('HiTeach_Version', authData.hostSoftwareVersion);
            console.log(`[${gameName}] 儲存 HiTeach 版本:`, authData.hostSoftwareVersion);
        }

        // 儲存 HiTeach 名單（如果有）
        if (authData.memberList && Array.isArray(authData.memberList)) {
            sessionStorage.setItem('ht_memberList', JSON.stringify(authData.memberList));
            console.log(`[${gameName}] 儲存 HiTeach 名單 (${authData.memberList.length} 位學生):`, authData.memberList);
        }

        // 儲存 class_id（只在有實際值時更新，避免覆蓋已有的正確值）
        if (authData.class_id !== undefined && authData.class_id !== null && authData.class_id !== '') {
            sessionStorage.setItem('ht_class_id', authData.class_id);
            console.log(`[${gameName}] 儲存 class_id:`, authData.class_id);
        } else if (authData.class_id !== undefined) {
            // 如果回應中 class_id 是空值，記錄警告但不覆蓋原有值
            console.warn(`[${gameName}] 收到空的 class_id (${authData.class_id})，保留原有值:`, sessionStorage.getItem('ht_class_id'));
        }

        // 儲存校園版授權狀態（如果有）
        if (authData.school_vers !== undefined) {
            localStorage.setItem('school_vers', String(authData.school_vers));
            console.log(`[${gameName}] 校園版授權狀態:`, authData.school_vers);
        }

        // 如果有 id_Token，解碼並儲存 profile
        if (authData.id_Token) {
            try {
                const decoded = jwt_decode(authData.id_Token);
                const profile = {
                    name: decoded.name || '使用者',
                    id: authData.login_Id,
                    picture: decoded.picture || ''
                };
                localStorage.setItem('profile', JSON.stringify(profile));
            } catch (decodeError) {
                console.error(`[${gameName}] 解碼 id_token 失敗:`, decodeError);
            }
        }

        // 設定 jumpFrom 標記
        sessionStorage.setItem('jumpFrom', 'HT');

        console.log(`[${gameName}] 驗證資料已儲存完成`);
    } catch (error) {
        console.error(`[${gameName}] 設定使用者驗證時發生錯誤:`, error);
    }
};

/**
 * 設定使用者驗證資料（來自 HiTeachCC.Auth）
 * @param {Object} authData - 包含 accessToken, idToken, expiresIn 欄位
 * @param {String} gameName - 遊戲名稱，用於 console.log 識別
 */
export const setupHiTeachCCAuth = (authData, gameName = 'Game') => {
    try {
        console.log(`[${gameName}] 設定 HiTeachCC 驗證資料:`, authData);

        const { accessToken, idToken, expiresIn } = authData;

        // 儲存 Token 到 localStorage
        if (accessToken) {
            localStorage.setItem('access_token', accessToken);
        }
        if (idToken) {
            localStorage.setItem('id_token', idToken);
        }
        if (expiresIn) {
            localStorage.setItem('expires_in', expiresIn.toString());
        }

        // 解碼 JWT 取得用戶資訊並儲存 Profile
        if (idToken) {
            try {
                const decoded = jwt_decode(idToken);
                const profileData = {
                    id: decoded.sub,
                    teammodelId: decoded.sub,
                    name: decoded.name || '使用者',
                    picture: decoded.picture || ''
                };
                localStorage.setItem('profile', JSON.stringify(profileData));
                console.log(`[${gameName}] Profile 已儲存:`, profileData);
            } catch (decodeError) {
                console.error(`[${gameName}] 解碼 idToken 失敗:`, decodeError);
            }
        }

        // 設定 jumpFrom 標記（標記來自 HiTeachCC）
        sessionStorage.setItem('jumpFrom', 'HTCC');

        // iframe 模式下自動啟用校園版權益
        localStorage.setItem('school_vers', 'true');

        // 新 HTCC session 開始，清除所有遊戲設定（避免上一個 session 的 restoredData 汙染）
        ['PvPGameConfig', 'MoleGameConfig', 'SnakeGameConfig', 'MazeGameConfig', 'PvPTFGameConfig'].forEach(key => {
            sessionStorage.removeItem(key);
        });
        console.log(`[${gameName}] 已清除舊遊戲設定 sessionStorage`);

        console.log(`[${gameName}] HiTeachCC 驗證資料已儲存完成`);
    } catch (error) {
        console.error(`[${gameName}] 設定 HiTeachCC 驗證時發生錯誤:`, error);
    }
};

/**
 * 使用遊戲訊息橋接器的 Composable
 * @param {String} gameName - 遊戲名稱（例如：'Snake', 'Mole', 'Star'）
 * @param {Function} onInitGameHome - 收到 Init.GameHome 時的額外處理回調（可選）
 * @param {Function} onHiTeachCCAuth - 收到 HiTeachCC.Auth 時的額外處理回調（可選）
 * @returns {Object} - { messageBridge, setupUserAuth, setupHiTeachCCAuth, isInIframe }
 */
export const useGameMessageBridge = (gameName = 'Game', onInitGameHome = null, onHiTeachCCAuth = null) => {
    // 初始化消息橋接器
    const messageBridge = MessageBridge.init();

    // 儲存監聽器回調函數的引用，以便在 onUnmounted 時移除
    let messageHandler = null;

    onMounted(() => {
        // 定義訊息處理函數
        messageHandler = (event) => {
            const msg = event.data;

            // 監聽 Init.GameHome 訊息（來自 HiTeach 桌面版 WebView）
            if (msg.action === 'Init.GameHome') {
                console.log(`[${gameName}] 收到 Init.GameHome 訊息:`, msg);

                // 執行使用者驗證設定
                setupUserAuth(msg.data, gameName);

                // 如果有額外的處理回調，執行它
                if (onInitGameHome && typeof onInitGameHome === 'function') {
                    onInitGameHome(msg.data);
                }
            }

            // 監聯 HiTeachCC.Auth 訊息（來自 HiTeachCC iframe）
            if (msg.action === 'HiTeachCC.Auth' || msg.action === 'HiTeachCC.TokenRefresh') {
                // Origin 白名單驗證
                if (event.origin && !HITEACHCC_ALLOWED_ORIGINS.includes(event.origin)) {
                    console.warn(`[${gameName}] 拒絕來自未授權來源的 HiTeachCC 訊息:`, event.origin);
                    return;
                }

                const isInitialAuth = msg.action === 'HiTeachCC.Auth';
                console.log(`[${gameName}] 收到 ${isInitialAuth ? 'HiTeachCC.Auth' : 'HiTeachCC.TokenRefresh'} 訊息:`, msg);

                // 執行 HiTeachCC 驗證設定
                setupHiTeachCCAuth(msg, gameName);

                // 如果有額外的處理回調，執行它
                if (onHiTeachCCAuth && typeof onHiTeachCCAuth === 'function') {
                    onHiTeachCCAuth(msg, isInitialAuth);
                }
            }
        };

        // 註冊監聽器
        messageBridge.addEventListener(messageHandler);
        console.log(`[${gameName}] 訊息監聽器已註冊 (Init.GameHome + HiTeachCC.Auth)`);

        // 如果在 iframe 環境中，發送 Ready 訊息給父視窗
        if (isInIframe()) {
            console.log(`[${gameName}] 偵測到在 iframe 中運行，發送 GamePage.Ready 給父視窗`);
            setTimeout(() => {
                messageBridge.postMessageToParent({ action: 'GamePage.Ready', gameName: gameName }, '*');
            }, 100);
        }
    });

    onUnmounted(() => {
        // 清理監聯器
        if (messageHandler) {
            messageBridge.removeEventListener(messageHandler);
            console.log(`[${gameName}] 訊息監聽器已清理`);
            messageHandler = null;
        }
    });

    return {
        messageBridge,
        setupUserAuth: (authData) => setupUserAuth(authData, gameName),
        setupHiTeachCCAuth: (authData) => setupHiTeachCCAuth(authData, gameName),
        isInIframe
    };
};

export default useGameMessageBridge;
